import type { ComponentType, SVGProps } from 'react';

/**
 * Icon-Baustein (Rudolf 04.11.2026: Größen und Strichstärken zentral). Setzt Größe und Strichstärke nur über die Tokens
 * `--icon-*` aus styles.css; `size={14}` oder `strokeWidth={1.75}` direkt am Lucide-Icon sind rot (`till-design/tokens`,
 * Kategorie `icon` bzw. `linie`).
 *
 * Größen: `xs` 12 px (Zähler, Randsymbole), `sm` 14 px (Zeilen, Chips), `base` 16 px (Standard in Buttons und Feldern),
 * `md` 18 px (Kopf, Hinweise; der Name `--icon-md` gehörte schon vorher zu 18 px), `lg` 20 px (Kacheln, leere Zustände). Strichstärken: `light` 1,5 (Standard, feine Linien),
 * `regular` 1,75, `strong` 2, `bold` 2,5 (Häkchen, Schließen).
 *
 * Beispiel: `<Icon icon={Eye} size="sm" />` statt `<Eye size={14} strokeWidth={1.5} />`.
 */
export type IconSize = 'xs' | 'sm' | 'base' | 'md' | 'lg';
export type IconStroke = 'light' | 'regular' | 'strong' | 'bold';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'size' | 'stroke'> {
  /** Lucide-Icon (oder jede Komponente, die SVG-Eigenschaften annimmt). */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  size?: IconSize;
  stroke?: IconStroke;
}

export function Icon({ icon: Glyph, size = 'base', stroke = 'light', style, ...rest }: IconProps) {
  return (
    <Glyph
      aria-hidden={rest['aria-label'] || rest['aria-labelledby'] ? undefined : true}
      {...rest}
      style={{ width: `var(--icon-${size})`, height: `var(--icon-${size})`, strokeWidth: `var(--icon-stroke-${stroke})`, ...style }}
    />
  );
}
